import React, { Suspense } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { router } from "./router";
import { AuthProvider } from "./auth/AuthProvider";
import { installIdleActivityListeners } from "./lib/idle";
import { auth } from "./firebase";
import { IS_DEV, IS_PREVIEW } from "./config/featureFlags";
import { registerSW, unregisterSW } from "@/register-sw";
import { BUILD_INFO, BUILD_TAG } from "./buildInfo";

console.info("[BOOT] main.jsx executing");
// Auto-reload when a lazy-loaded chunk is missing after a new deployment.
// Vite fires this event when a dynamic import 404s and the response is not JS.
// This happens when the user has a stale index.html (open tab across a deploy).
window.addEventListener('vite:preloadError', (event) => {
  console.warn('[ThreadLock] Stale chunk detected, reloading...', event.payload);
  window.location.reload();
});
// Log build info for diagnostics (always visible in production console)
// Cryptographically tied to exact commit - no manual editing possible
// v2.2.16: feat(marketplace): Gate subscriber-facing pages with Coming Soon until attorneys onboarded
// v2.2.17: feat(marketplace): Dynamic per-jurisdiction availability via useMarketplaceAvailability
// v2.2.18: fix(auth,billing,inbox,smartbox): subscription sync gap, SmartBox schema, Inbox crash
console.info(`[ThreadLock] Build: v2.3.1 (76cfe85b 2026-05-17T18:19:22Z)`);


// T0: App boot timestamp for auth timing diagnostics
const bootTimestamp = new Date().toISOString();
console.log(`[T0-BOOT] App initialized at ${bootTimestamp}`);

// DEPLOYMENT FORENSICS: Log complete build fingerprint
// This proves which exact commit is running in production
/* eslint-disable no-restricted-syntax */
console.info('[ThreadLock] Build Info:', {
  sha: BUILD_INFO.gitSha,
  shaShort: BUILD_INFO.gitShaShort,
  branch: BUILD_INFO.gitBranch,
  version: BUILD_INFO.appVersion,
  buildTime: BUILD_INFO.buildTimeUtc,
  environment: BUILD_INFO.environment,
  // CI fingerprint (if available)
  ciRepo: import.meta.env.VITE_GITHUB_REPOSITORY || 'n/a',
  ciSha: import.meta.env.VITE_GITHUB_SHA || 'n/a',
  ciRef: import.meta.env.VITE_GITHUB_REF_NAME || 'n/a'
});
/* eslint-enable no-restricted-syntax */

// Helios Forensic Protocol - CRITICAL: Load telemetry FIRST before any other app code
// This ensures console overrides, fetch wrapper, and window.telemetry are available immediately
import "./lib/telemetry";

// Import index.css which includes tokens.css before Tailwind directives
import "./index.css";

// Import print stylesheet for PDF/print optimization
import "./styles/print.css";

// Cypress diagnostic flags (fail-loud: initialize immediately)
window.__TL_BOOT_TS__ = Date.now();
window.__TL_APP_BOOTED__ = true;  // Set immediately - React mount is synchronous
window.__TL_APP_BOOTED_TS__ = Date.now();
window.__TL_AUTH_READY__ = false; // Set by onAuthStateChanged
window.__TL_AUTH_UID__ = null;

// Accessibility auditing in development
if (IS_DEV) {
  import('@axe-core/react').then((axe) => {
    axe.default(React, createRoot, 1000, {
      // Configuration
      rules: [
        {
          id: 'color-contrast',
          enabled: true,
        },
        {
          id: 'image-alt',
          enabled: true,
        },
        {
          id: 'label',
          enabled: true,
        },
        {
          id: 'button-name',
          enabled: true,
        },
        {
          id: 'aria-required-attr',
          enabled: true,
        },
        {
          id: 'aria-valid-attr-value',
          enabled: true,
        },
      ],
    });
    console.info('[a11y] axe-core enabled - check console for accessibility violations');
  });
}

// Create QueryClient with optimal defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
      gcTime: 10 * 60 * 1000, // 10 minutes - cache garbage collection
      retry: 1, // Retry failed requests once
      refetchOnWindowFocus: false, // Don't refetch on window focus (can be annoying)
    },
  },
});

// App version for cache busting - FORCE NEW BUNDLE
window.__APP_BUILD__ = "v1.13.0-20251130-2049-auth-hardening-complete";

// Auth state listener - unconditional registration
auth.onAuthStateChanged((u) => {
  // Set auth readiness flags (used by Cypress tests)
  window.__TL_AUTH_READY__ = true;
  window.__TL_AUTH_UID__ = u?.uid ?? null;
  
  if (IS_DEV) {
    console.info("[auth] user", !!u, u?.uid);
    console.info("[AUTH_READY]", { uid: window.__TL_AUTH_UID__ });
  }
});

// Always clear stale SWs in dev/preview
if (IS_DEV || IS_PREVIEW) {
  unregisterSW();
}

registerSW();

console.info("[BOOT] Preparing to mount React app...");
const el = document.getElementById("root");
console.info("[BOOT] #root element found:", !!el);

try {
  const KEY = "idle:lastActivityTs";
  localStorage.setItem(KEY, String(Date.now()));
} catch {
  // Ignore localStorage write failures (private browsing, etc.)
}

console.info("[BOOT] Installing idle listeners...");
installIdleActivityListeners();

console.info("[BOOT] Creating React root...");
const root = createRoot(el);
console.info("[BOOT] React root created, calling render()...");

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Suspense fallback={<div className="flex min-h-screen items-center justify-center bg-transparent text-slate-100"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-orange"></div></div>}>
          <RouterProvider router={router} />
        </Suspense>
      </AuthProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
console.info("[BOOT] React mount complete");

// 🎯 CRYPTOGRAPHIC MOUNT BEACON: Proof app mounted successfully
// Used by E2E smoke tests to verify React rendered before proceeding
try {
  window.__TL_APP_MOUNTED__ = {
    buildTag: "SIMPLIFIED_TEST",
    ts: Date.now(),
    env: "production"
  };
  console.info("[BOOT] Mount beacon set:", window.__TL_APP_MOUNTED__);
} catch (err) {
  console.error("[BOOT] FAILED to set mount beacon:", err);
}
